import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import translate from '@translate';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableRow from '@mui/material/TableRow';
import FormBlock from 'src/components/FormBlock';
import JsonCodeViewer from 'src/components/JsonCodeViewer';
import Table from 'src/components/Table';
import Typography from 'src/components/Typography';
import { getRowElementText } from 'src/components/Table/functions';
import { CustomRenderType, CustomRowRenderType, TablePropsOptionsType } from 'src/types/table';
import { WebhookDeliveryAttemptType } from 'src/types/webhookDeliveriesGrid';
import { getStatusColor } from '../WebhookRecordsListContainer/functions';
import { attemptColumns } from './constants';

const attemptTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ATTEMPT', direction: 'ASC' },
    page: 1,
    items: 100,
    columns: attemptColumns,
};

type AttemptHistorySectionProps = {
    attempts: WebhookDeliveryAttemptType[];
};

const AttemptHistorySection: FC<AttemptHistorySectionProps> = (props) => {
    const { attempts } = props;
    const [selectedAttempt, setSelectedAttempt] = useState<number | null>(null);

    const sortedAttempts = useMemo(
        () => [...attempts].sort((left, right) => left.ATTEMPT - right.ATTEMPT),
        [attempts],
    );

    useEffect(() => {
        if (!sortedAttempts.length) {
            setSelectedAttempt(null);
            return;
        }
        setSelectedAttempt((current) => {
            if (current !== null && sortedAttempts.some((attempt) => attempt.ATTEMPT === current)) {
                return current;
            }
            return sortedAttempts[sortedAttempts.length - 1].ATTEMPT;
        });
    }, [sortedAttempts]);

    const selectedAttemptData = sortedAttempts.find((attempt) => attempt.ATTEMPT === selectedAttempt) ?? null;

    const customRender: CustomRenderType = (column, row): ReactNode | string => {
        if (column.key === 'STATUS_CODE') {
            return (
                <span style={{ color: getStatusColor(row.STATUS_CODE as number) }}>{row.STATUS_CODE}</span>
            );
        }
        return undefined;
    };

    const customRowRender: CustomRowRenderType = (params) => {
        const { row, rowIdKey, visilbeColumns, selectOptions } = params;
        const attempt = Number(row.ATTEMPT);
        const isSelected = selectedAttempt === attempt;

        return (
            <MuiTableRow
                key={String(row[rowIdKey])}
                onClick={() => setSelectedAttempt(attempt)}
                sx={{
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#e3f2fd' : undefined,
                }}
                className={isSelected ? 'table-row-selected' : ''}
                data-testid={`webhook-record-detail-attempt-row-${attempt}`}
            >
                {visilbeColumns.map((column) => {
                    const { text, element } = getRowElementText(
                        row,
                        column,
                        customRender,
                        selectOptions,
                        { formatDisplay: true },
                    );
                    return (
                        <MuiTableCell key={column.key} title={text} style={column.customStyle}>
                            {element || text}
                        </MuiTableCell>
                    );
                })}
            </MuiTableRow>
        );
    };

    return (
        <>
            <FormBlock>
                <Typography variant="h6" gutterBottom>
                    {translate('webhookRecordDetail.attempts')}
                </Typography>
                <Typography variant="body2" gutterBottom>
                    {translate('webhookRecordDetail.attemptsHint')}
                </Typography>
                <div data-testid="webhook-record-detail-attempts-table">
                <Table
                    allColumns={attemptColumns}
                    rowIdKey="ATTEMPT"
                    selectRowIdKey="ATTEMPT"
                    rows={sortedAttempts.map((attempt) => ({ ...attempt }))}
                    loading={false}
                    columsLabelPrefix="webhookRecords.table"
                    options={attemptTableOptions}
                    totalCount={sortedAttempts.length}
                    onChangeOptions={() => undefined}
                    rowActions={[]}
                    selectOptions={{}}
                    fullHeight={false}
                    customRender={customRender}
                    customRowRender={customRowRender}
                />
                </div>
            </FormBlock>
            {selectedAttemptData && (
                <FormBlock>
                    <JsonCodeViewer
                        value={selectedAttemptData.RESPONSE_MESSAGE || ''}
                        label={translate('webhookRecordDetail.attemptResponse', {
                            attempt: selectedAttemptData.ATTEMPT,
                        })}
                        testId={`webhook-record-detail-attempt-response-${selectedAttemptData.ATTEMPT}`}
                        minHeight={200}
                    />
                </FormBlock>
            )}
        </>
    );
};

export default AttemptHistorySection;
